import React, {useEffect, useState} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {useUserServiceGetApiUserDomain, useUserServiceGetApiUserMe} from '../api/generated/queries/queries';
import DeleteConfirmDialog from '../components/Common/DeleteConfirmDialog';
import {
  AcquisitionTypesService,
  LeadAttributesService,
  OffersService,
  PrefixConfigControllerService,
  UserService
} from '../api/generated/requests/services.gen';
import {
  AcquisitionTypeDTO,
  CurrentUserInfoDTO,
  OfferDTO,
  PredefinedLeadAttributeDTO,
  PrefixConfiguration
} from '../api/generated/requests/types.gen';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminSettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Mapping function for attribute types from English to Polish
  const getAttributeTypeLabel = (attributeType: string): string => {
    const typeMapping: Record<string, string> = {
      'TEXT': 'Tekst',
      'NUMBER': 'Liczba',
      'DATE': 'Data',
      'BOOLEAN': 'Tak/Nie'
    };
    return typeMapping[attributeType] || attributeType;
  };

  // Helper function to get user initials
  const getUserInitials = (user: CurrentUserInfoDTO): string => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };
  
  // Loading states
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isPromotingUser, setIsPromotingUser] = useState<string | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState<string | null>(null);
  const [isCreatingAcquisition, setIsCreatingAcquisition] = useState(false);
  const [isUpdatingAcquisition, setIsUpdatingAcquisition] = useState<number | null>(null);
  const [isDeletingAcquisition, setIsDeletingAcquisition] = useState<number | null>(null);
  const [isCreatingAttribute, setIsCreatingAttribute] = useState(false);
  const [isUpdatingAttribute, setIsUpdatingAttribute] = useState<number | null>(null);
  const [isDeletingAttribute, setIsDeletingAttribute] = useState<number | null>(null);
  const [isCreatingOffer, setIsCreatingOffer] = useState(false);
  const [isDeletingOffer, setIsDeletingOffer] = useState<number | null>(null);
  const [isUpdatingOffer, setIsUpdatingOffer] = useState<number | null>(null);
  const [isUpdatingPrefix, setIsUpdatingPrefix] = useState(false);
  
  // User management state
  const [users, setUsers] = useState<CurrentUserInfoDTO[]>([]);
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', firstName: '', lastName: '', login: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  
  // Acquisition types state
  const [acquisitionTypes, setAcquisitionTypes] = useState<AcquisitionTypeDTO[]>([]);
  const [newAcquisitionDialog, setNewAcquisitionDialog] = useState(false);
  const [editAcquisitionDialog, setEditAcquisitionDialog] = useState(false);
  const [newAcquisition, setNewAcquisition] = useState({ name: '' });
  const [editingAcquisition, setEditingAcquisition] = useState<AcquisitionTypeDTO | null>(null);
  
  // Lead attributes state
  const [leadAttributes, setLeadAttributes] = useState<PredefinedLeadAttributeDTO[]>([]);
  const [newAttributeDialog, setNewAttributeDialog] = useState(false);
  const [editAttributeDialog, setEditAttributeDialog] = useState(false);
  const [newAttribute, setNewAttribute] = useState({ attributeKey: '', attributeType: 'TEXT' });
  const [editingAttribute, setEditingAttribute] = useState<PredefinedLeadAttributeDTO | null>(null);

  // Offers state
  const [offers, setOffers] = useState<OfferDTO[]>([]);
  const [newOfferDialog, setNewOfferDialog] = useState(false);
  const [editOfferDialog, setEditOfferDialog] = useState(false);
  const [newOffer, setNewOffer] = useState({ name: '', type: 'PRODUCT' as 'PRODUCT' | 'SERVICE', description: '', price: 0 });
  const [editingOffer, setEditingOffer] = useState<OfferDTO | null>(null);
  
  // Prefix configuration state
  const [prefixConfig, setPrefixConfig] = useState<PrefixConfiguration[]>([]);
  const [editPrefixDialog, setEditPrefixDialog] = useState(false);
  const [editingPrefix, setEditingPrefix] = useState<PrefixConfiguration | null>(null);
  

  // Search state for each tab
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [acquisitionSearchTerm, setAcquisitionSearchTerm] = useState('');
  const [attributeSearchTerm, setAttributeSearchTerm] = useState('');
  const [offerSearchTerm, setOfferSearchTerm] = useState('');
  const [prefixSearchTerm, setPrefixSearchTerm] = useState('');

  // Delete confirmation dialog states
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState<CurrentUserInfoDTO | null>(null);
  const [deleteAcquisitionDialog, setDeleteAcquisitionDialog] = useState(false);
  const [selectedAcquisitionToDelete, setSelectedAcquisitionToDelete] = useState<AcquisitionTypeDTO | null>(null);
  const [deleteAttributeDialog, setDeleteAttributeDialog] = useState(false);
  const [selectedAttributeToDelete, setSelectedAttributeToDelete] = useState<PredefinedLeadAttributeDTO | null>(null);
  const [deleteOfferDialog, setDeleteOfferDialog] = useState(false);
  const [selectedOfferToDelete, setSelectedOfferToDelete] = useState<OfferDTO | null>(null);

  const { data: currentUser, isLoading: isLoadingUser, error: userError } = useUserServiceGetApiUserMe();
  const { data: domainUsers, refetch: refetchDomainUsers } = useUserServiceGetApiUserDomain();

  // Check if user is admin
  const isAdmin = currentUser?.roles?.includes('api-admin');

  // Filtering functions for search
  const filteredUsers = users.filter(user => {
    if (!userSearchTerm) return true;
    const searchLower = userSearchTerm.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email?.toLowerCase() || '';
    const roles = user.roles?.join(' ').toLowerCase() || '';
    return fullName.includes(searchLower) || email.includes(searchLower) || roles.includes(searchLower);
  }).sort((a, b) => {
    // Sort by lastName
    const lastNameA = a.lastName?.toLowerCase() || '';
    const lastNameB = b.lastName?.toLowerCase() || '';
    return lastNameA.localeCompare(lastNameB);
  });

  // Count administrators
  const adminCount = users.filter(user => user.roles?.includes('api-admin')).length;

  const filteredAcquisitionTypes = acquisitionTypes.filter(type => {
    if (!acquisitionSearchTerm) return true;
    const searchLower = acquisitionSearchTerm.toLowerCase();
    return type.name?.toLowerCase().includes(searchLower);
  });

  const filteredLeadAttributes = leadAttributes.filter(attribute => {
    if (!attributeSearchTerm) return true;
    const searchLower = attributeSearchTerm.toLowerCase();
    const attributeKey = attribute.attributeKey?.toLowerCase() || '';
    const attributeType = getAttributeTypeLabel(attribute.attributeType || '').toLowerCase();
    return attributeKey.includes(searchLower) || attributeType.includes(searchLower);
  });

  const filteredOffers = offers.filter(offer => {
    if (!offerSearchTerm) return true;
    const searchLower = offerSearchTerm.toLowerCase();
    const name = offer.name?.toLowerCase() || '';
    const type = offer.type?.toLowerCase() || '';
    const description = offer.description?.toLowerCase() || '';
    return name.includes(searchLower) || type.includes(searchLower) || description.includes(searchLower);
  });

  const filteredPrefixConfig = prefixConfig.filter(config => {
    if (!prefixSearchTerm) return true;
    const searchLower = prefixSearchTerm.toLowerCase();
    const prefixType = config.prefixType?.toLowerCase() || '';
    const prefixValue = config.prefixValue?.toLowerCase() || '';
    return prefixType.includes(searchLower) || prefixValue.includes(searchLower);
  });

  useEffect(() => {
    if (!isAdmin) return;
    
    loadData();
  }, [isAdmin]);

  // Update users when domainUsers hook data changes
  useEffect(() => {
    if (domainUsers) {
      setUsers(domainUsers);
    }
  }, [domainUsers]);

  const loadData = async () => {
    try {
      // Users are now loaded via hook data in useEffect above
      
      // Load acquisition types
      const acquisitionResponse = await AcquisitionTypesService.getAcquisitionTypes();
      // Sort acquisition types by ID in ascending order
      const sortedAcquisitionTypes = acquisitionResponse.sort((a, b) => {
        const aId = a.id || 0;
        const bId = b.id || 0;
        return aId - bId;
      });
      setAcquisitionTypes(sortedAcquisitionTypes);
      
      // Load lead attributes
      const attributesResponse = await LeadAttributesService.getApiPredefinedLeadAttributes();
      // Sort lead attributes by ID in ascending order
      const sortedLeadAttributes = attributesResponse.sort((a, b) => {
        const aId = a.id || 0;
        const bId = b.id || 0;
        return aId - bId;
      });
      setLeadAttributes(sortedLeadAttributes);
      
      // Load offers
      const offersResponse = await OffersService.getApiOffers();
      // Sort offers by ID in ascending order
      const sortedOffers = offersResponse.sort((a, b) => {
        const aId = a.id || 0;
        const bId = b.id || 0;
        return aId - bId;
      });
      setOffers(sortedOffers);
      
      // Load prefix configuration
      const prefixResponse = await PrefixConfigControllerService.getApiConfigPrefixAll();
      // Sort prefix configuration by ID in ascending order
      const sortedPrefixConfig = prefixResponse.sort((a, b) => {
        const aId = a.id || 0;
        const bId = b.id || 0;
        return aId - bId;
      });
      setPrefixConfig(sortedPrefixConfig);
      
    } catch (error) {
      showSnackbar('Błąd podczas ładowania danych', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Delete confirmation dialog handlers
  const handleOpenDeleteUserDialog = (user: CurrentUserInfoDTO) => {
    setSelectedUserToDelete(user);
    setDeleteUserDialog(true);
  };

  const handleCloseDeleteUserDialog = () => {
    setDeleteUserDialog(false);
    setSelectedUserToDelete(null);
  };

  const handleOpenDeleteAcquisitionDialog = (acquisition: AcquisitionTypeDTO) => {
    setSelectedAcquisitionToDelete(acquisition);
    setDeleteAcquisitionDialog(true);
  };

  const handleCloseDeleteAcquisitionDialog = () => {
    setDeleteAcquisitionDialog(false);
    setSelectedAcquisitionToDelete(null);
  };

  const handleOpenDeleteAttributeDialog = (attribute: PredefinedLeadAttributeDTO) => {
    setSelectedAttributeToDelete(attribute);
    setDeleteAttributeDialog(true);
  };

  const handleCloseDeleteAttributeDialog = () => {
    setDeleteAttributeDialog(false);
    setSelectedAttributeToDelete(null);
  };

  const handleOpenDeleteOfferDialog = (offer: OfferDTO) => {
    setSelectedOfferToDelete(offer);
    setDeleteOfferDialog(true);
  };

  const handleCloseDeleteOfferDialog = () => {
    setDeleteOfferDialog(false);
    setSelectedOfferToDelete(null);
  };


  // Password validation function
  const validatePassword = (password: string, confirmPassword: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Hasło musi mieć co najmniej 8 znaków');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jedną małą literę');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jedną wielką literę');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jedną cyfrę');
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jeden znak specjalny');
    }
    if (password !== confirmPassword) {
      errors.push('Hasła muszą być takie same');
    }
    
    return errors;
  };

  // Handle password change with validation
  const handlePasswordChange = (field: 'password' | 'confirmPassword', value: string) => {
    const updatedUser = { ...newUser, [field]: value };
    setNewUser(updatedUser);
    
    // Validate passwords if both fields have values
    if (updatedUser.password || updatedUser.confirmPassword) {
      const errors = validatePassword(updatedUser.password, updatedUser.confirmPassword);
      setPasswordErrors(errors);
    } else {
      setPasswordErrors([]);
    }
  };

  // User management functions
  const handleCreateUser = async () => {
    if (isCreatingUser) return;
    
    // Validate passwords before submission
    const errors = validatePassword(newUser.password, newUser.confirmPassword);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      showSnackbar('Proszę poprawić błędy w haśle', 'error');
      return;
    }
    
    // Check if all required fields are filled
    if (!newUser.login || !newUser.email || !newUser.firstName || !newUser.lastName || !newUser.password) {
      showSnackbar('Wszystkie pola są wymagane', 'error');
      return;
    }
    
    try {
      setIsCreatingUser(true);
      await UserService.postApiUser({
        requestBody: {
          email: newUser.email,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          login: newUser.login,
          password: newUser.password,
        }
      });
      setNewUserDialog(false);
      setNewUser({ email: '', firstName: '', lastName: '', login: '', password: '', confirmPassword: '' });
      setPasswordErrors([]);
      setShowPassword(false);
      setShowConfirmPassword(false);
      refetchDomainUsers();
      showSnackbar('Użytkownik został utworzony', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas tworzenia użytkownika: ' + (error as any)?.body?.message || 'Nieznany błąd', 'error');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handlePromoteUser = async (userId: string) => {
    if (isPromotingUser === userId) return;
    
    try {
      setIsPromotingUser(userId);
      await UserService.postApiUserByUserIdPromoteToAdmin({ userId });
      refetchDomainUsers();
      showSnackbar('Użytkownik został awansowany na administratora', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas awansowania użytkownika', 'error');
    } finally {
      setIsPromotingUser(null);
    }
  };

  const handleDemoteUser = async (userId: string) => {
    if (isPromotingUser === userId) return;
    
    try {
      setIsPromotingUser(userId);
      await UserService.postApiUserByUserIdDemoteFromAdmin({ userId });
      refetchDomainUsers();
      showSnackbar('Użytkownik został zdegradowany do roli użytkownika', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas degradacji użytkownika', 'error');
    } finally {
      setIsPromotingUser(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserToDelete?.id || isDeletingUser === selectedUserToDelete.id) return;
    
    try {
      setIsDeletingUser(selectedUserToDelete.id);
      await UserService.deleteApiUserByUserId({ userId: selectedUserToDelete.id });
      refetchDomainUsers();
      showSnackbar('Użytkownik został usunięty', 'success');
      handleCloseDeleteUserDialog();
    } catch (error) {
      showSnackbar('Błąd podczas usuwania użytkownika', 'error');
    } finally {
      setIsDeletingUser(null);
    }
  };

  // Acquisition types functions
  const handleCreateAcquisitionType = async () => {
    if (isCreatingAcquisition) return;
    
    try {
      setIsCreatingAcquisition(true);
      await AcquisitionTypesService.postAcquisitionTypes({
        requestBody: {
          name: newAcquisition.name,
        }
      });
      setNewAcquisitionDialog(false);
      setNewAcquisition({ name: '' });
      loadData();
      showSnackbar('Żródło pozyskania zostało utworzone', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas tworzenia źródła pozyskania', 'error');
    } finally {
      setIsCreatingAcquisition(false);
    }
  };

  const handleUpdateAcquisitionType = async () => {
    if (!editingAcquisition?.id || isUpdatingAcquisition === editingAcquisition.id) return;
    
    try {
      setIsUpdatingAcquisition(editingAcquisition.id);
      await AcquisitionTypesService.putAcquisitionTypesById({
        id: editingAcquisition.id,
        requestBody: {
          name: editingAcquisition.name,
        }
      });
      setEditAcquisitionDialog(false);
      setEditingAcquisition(null);
      loadData();
      showSnackbar('Źródło pozyskania zostało zaktualizowane', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas aktualizacji źródła pozyskania', 'error');
    } finally {
      setIsUpdatingAcquisition(null);
    }
  };

  const handleDeleteAcquisitionType = async () => {
    if (!selectedAcquisitionToDelete?.id || isDeletingAcquisition === selectedAcquisitionToDelete.id) return;
    
    try {
      setIsDeletingAcquisition(selectedAcquisitionToDelete.id);
      await AcquisitionTypesService.deleteAcquisitionTypesById({ id: selectedAcquisitionToDelete.id });
      loadData();
      showSnackbar('Źródło pozyskania został usunięty', 'success');
      handleCloseDeleteAcquisitionDialog();
    } catch (error) {
      showSnackbar('Błąd podczas usuwania źródła pozyskania', 'error');
    } finally {
      setIsDeletingAcquisition(null);
    }
  };

  // Lead attributes functions
  const handleCreateLeadAttribute = async () => {
    if (isCreatingAttribute) return;
    
    try {
      setIsCreatingAttribute(true);
      await LeadAttributesService.postApiPredefinedLeadAttributes({
        requestBody: {
          attributeKey: newAttribute.attributeKey,
          attributeType: newAttribute.attributeType,
        }
      });
      setNewAttributeDialog(false);
      setNewAttribute({ attributeKey: '', attributeType: 'TEXT' });
      loadData();
      showSnackbar('Atrybut został utworzony', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas tworzenia atrybutu', 'error');
    } finally {
      setIsCreatingAttribute(false);
    }
  };

  const handleUpdateLeadAttribute = async () => {
    if (!editingAttribute?.id || isUpdatingAttribute === editingAttribute.id) return;
    
    try {
      setIsUpdatingAttribute(editingAttribute.id);
      await LeadAttributesService.putApiPredefinedLeadAttributesById({
        id: editingAttribute.id,
        requestBody: {
          attributeKey: editingAttribute.attributeKey,
          attributeType: editingAttribute.attributeType,
        }
      });
      setEditAttributeDialog(false);
      setEditingAttribute(null);
      loadData();
      showSnackbar('Atrybut został zaktualizowany', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas aktualizacji atrybutu', 'error');
    } finally {
      setIsUpdatingAttribute(null);
    }
  };

  const handleDeleteLeadAttribute = async () => {
    if (!selectedAttributeToDelete?.id || isDeletingAttribute === selectedAttributeToDelete.id) return;
    
    try {
      setIsDeletingAttribute(selectedAttributeToDelete.id);
      await LeadAttributesService.deleteApiPredefinedLeadAttributesById({ id: selectedAttributeToDelete.id });
      loadData();
      showSnackbar('Atrybut został usunięty', 'success');
      handleCloseDeleteAttributeDialog();
    } catch (error) {
      showSnackbar('Błąd podczas usuwania atrybutu', 'error');
    } finally {
      setIsDeletingAttribute(null);
    }
  };

  // Offers functions
  const handleCreateOffer = async () => {
    if (isCreatingOffer) return;
    
    try {
      setIsCreatingOffer(true);
      await OffersService.postApiOffers({
        requestBody: {
          name: newOffer.name,
          type: newOffer.type,
          description: newOffer.description,
          price: newOffer.price,
        }
      });
      setNewOfferDialog(false);
      setNewOffer({ name: '', type: 'PRODUCT', description: '', price: 0 });
      loadData();
      showSnackbar('Oferta została utworzona', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas tworzenia oferty', 'error');
    } finally {
      setIsCreatingOffer(false);
    }
  };

  const handleUpdateOffer = async () => {
    if (!editingOffer?.id || isUpdatingOffer === editingOffer.id) return;
    
    try {
      setIsUpdatingOffer(editingOffer.id);
      await OffersService.putApiOffersById({
        id: editingOffer.id,
        requestBody: {
          name: editingOffer.name,
          type: editingOffer.type,
          description: editingOffer.description,
          price: editingOffer.price,
        }
      });
      setEditOfferDialog(false);
      setEditingOffer(null);
      loadData();
      showSnackbar('Oferta została zaktualizowana', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas aktualizacji oferty', 'error');
    } finally {
      setIsUpdatingOffer(null);
    }
  };

  const handleDeleteOffer = async () => {
    if (!selectedOfferToDelete?.id || isDeletingOffer === selectedOfferToDelete.id) return;
    
    try {
      setIsDeletingOffer(selectedOfferToDelete.id);
      await OffersService.deleteApiOffersById({ id: selectedOfferToDelete.id });
      loadData();
      showSnackbar('Oferta została usunięta', 'success');
      handleCloseDeleteOfferDialog();
    } catch (error) {
      showSnackbar('Błąd podczas usuwania oferty', 'error');
    } finally {
      setIsDeletingOffer(null);
    }
  };

  // Prefix configuration functions
  const handleUpdatePrefix = async () => {
    if (!editingPrefix || !editingPrefix.prefixType || isUpdatingPrefix) return;
    
    try {
      setIsUpdatingPrefix(true);
      await PrefixConfigControllerService.putApiConfigPrefixByPrefixType({
        prefixType: editingPrefix.prefixType,
        requestBody: {
          prefixValue: editingPrefix.prefixValue || '',
        }
      });
      setEditPrefixDialog(false);
      setEditingPrefix(null);
      loadData();
      showSnackbar('Prefiks został zaktualizowany', 'success');
    } catch (error) {
      showSnackbar('Błąd podczas aktualizacji prefiksu', 'error');
    } finally {
      setIsUpdatingPrefix(false);
    }
  };


  // Show loading spinner while checking user permissions
  if (isLoadingUser) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Sprawdzanie uprawnień...
        </Typography>
      </Box>
    );
  }

  // Show error message only when user data has loaded and user is not admin
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nie masz uprawnień do przeglądania tej strony. Tylko administratorzy mogą zarządzać ustawieniami systemu.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ 
        width: '100%',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        bgcolor: '#ffffff',
        mb: 2
      }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin settings tabs">
          <Tab label="Użytkownicy" />
          <Tab label="Źródła pozyskania" />
          <Tab label="Atrybuty kontaków" />
          <Tab label="Oferty" />
          <Tab label="Płatności" />
          <Tab label="Prefiksy ID" />
        </Tabs>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            px: 2,
            py: 1.5,
            gap: 2
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
              Zarządzanie użytkownikami
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Szukaj użytkowników..."
                variant="outlined"
                size="small"
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  minWidth: '200px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '32px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiInputBase-input': {
                    paddingY: '6px',
                    fontSize: '0.8rem',
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewUserDialog(true)}
                sx={{
                  fontSize: '0.8rem',
                  minWidth: 'auto',
                  px: 2,
                  borderRadius: '9999px'
                }}
              >
                Dodaj użytkownika
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {filteredUsers.map((user) => (
                <Grid key={user.id} size={{xs: 12, md: 6, lg: 4}}>
                  <Card sx={{ height: '100%', minHeight: '160px', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ 
                          mr: 2, 
                          bgcolor: user.color || 'primary.main',
                          fontSize: '1rem',
                          fontWeight: 600
                        }}>
                          {getUserInitials(user)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        mt: 1,
                        flexWrap: 'nowrap',
                        justifyContent: 'flex-start'
                      }}>
                        <Chip
                          label={user.roles?.includes('api-admin') ? 'Administrator' : 'Użytkownik'}
                          color={user.roles?.includes('api-admin') ? 'primary' : 'default'}
                          size="small"
                        />
                        {!user.roles?.includes('api-admin') && (
                          <Button
                            size="small"
                            onClick={() => handlePromoteUser(user.id!)}
                            disabled={isPromotingUser === user.id}
                            startIcon={isPromotingUser === user.id ? <CircularProgress size={16} /> : undefined}
                            sx={{
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {isPromotingUser === user.id ? 'Awansowanie...' : 'Awansuj'}
                          </Button>
                        )}
                        {user.roles?.includes('api-admin') && isAdmin && user.id !== currentUser?.id && (
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => handleDemoteUser(user.id!)}
                            disabled={isPromotingUser === user.id}
                            startIcon={isPromotingUser === user.id ? <CircularProgress size={16} /> : undefined}
                            sx={{
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {isPromotingUser === user.id ? 'Degradowanie...' : 'odbierz uprawnienia'}
                          </Button>
                        )}
                        {user.id !== currentUser?.id && !(user.roles?.includes('api-admin') && adminCount >= 2) && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteUserDialog(user)}
                            disabled={isDeletingUser === user.id}
                            startIcon={isDeletingUser === user.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                            sx={{
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {isDeletingUser === user.id ? 'Usuwanie...' : 'Usuń'}
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Acquisition Types Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            px: 2,
            py: 1.5,
            gap: 2
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
               Źródła pozyskania
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Szukaj źródeł pozyskania..."
                variant="outlined"
                size="small"
                value={acquisitionSearchTerm}
                onChange={(e) => setAcquisitionSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  minWidth: '200px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '32px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiInputBase-input': {
                    paddingY: '6px',
                    fontSize: '0.8rem',
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewAcquisitionDialog(true)}
                sx={{
                  fontSize: '0.8rem',
                  minWidth: 'auto',
                  px: 2,
                  borderRadius: '9999px'
                }}
              >
                Dodaj źródło
              </Button>
            </Box>
          </Box>
          
          <List sx={{ p: 0 }}>
            {filteredAcquisitionTypes.map((type) => (
              <ListItem key={type.id} divider sx={{ px: 2 }}>
                <ListItemText
                  primary={type.name}
                  secondary={`Utworzono: ${type.createdAt ? new Date(type.createdAt).toLocaleDateString() : 'Brak daty'}`}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.8rem' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    size="small"
                    onClick={() => {
                      setEditingAcquisition(type);
                      setEditAcquisitionDialog(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    size="small"
                    onClick={() => handleOpenDeleteAcquisitionDialog(type)}
                    disabled={isDeletingAcquisition === type.id}
                  >
                    {isDeletingAcquisition === type.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Lead Attributes Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            px: 2,
            py: 1.5,
            gap: 2
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
              Dodatkowe atrybuty dla kontaków
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Szukaj atrybutów..."
                variant="outlined"
                size="small"
                value={attributeSearchTerm}
                onChange={(e) => setAttributeSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  minWidth: '200px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '32px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiInputBase-input': {
                    paddingY: '6px',
                    fontSize: '0.8rem',
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewAttributeDialog(true)}
                sx={{
                  fontSize: '0.8rem',
                  minWidth: 'auto',
                  px: 2,
                  borderRadius: '9999px'
                }}
              >
                Dodaj atrybut
              </Button>
            </Box>
          </Box>
          
          <List sx={{ p: 0 }}>
            {filteredLeadAttributes.map((attribute) => (
              <ListItem key={attribute.id} divider sx={{ px: 2 }}>
                <ListItemText
                  primary={attribute.attributeKey}
                  secondary={`Typ: ${getAttributeTypeLabel(attribute.attributeType || '')}`}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.8rem' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    size="small"
                    onClick={() => {
                      setEditingAttribute(attribute);
                      setEditAttributeDialog(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    size="small"
                    onClick={() => handleOpenDeleteAttributeDialog(attribute)}
                    disabled={isDeletingAttribute === attribute.id}
                  >
                    {isDeletingAttribute === attribute.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Offers Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            px: 2,
            py: 1.5,
            gap: 2
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
              Zarządzanie ofertami
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Szukaj ofert..."
                variant="outlined"
                size="small"
                value={offerSearchTerm}
                onChange={(e) => setOfferSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  minWidth: '200px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    height: '32px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiInputBase-input': {
                    paddingY: '6px',
                    fontSize: '0.8rem',
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewOfferDialog(true)}
                sx={{
                  fontSize: '0.8rem',
                  minWidth: 'auto',
                  px: 2,
                  borderRadius: '9999px'
                }}
              >
                Dodaj ofertę
              </Button>
            </Box>
          </Box>
          
          <List sx={{ p: 0 }}>
            {filteredOffers.map((offer) => (
              <ListItem key={offer.id} divider sx={{ px: 2 }}>
                <ListItemText
                  primary={offer.name}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Typ: {offer.type === 'PRODUCT' ? 'Produkt' : 'Usługa'} | Cena: {offer.price}zł
                      </Typography>
                      {offer.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {offer.description}
                        </Typography>
                      )}
                    </Box>
                  }
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.8rem' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    size="small"
                    onClick={() => {
                      setEditingOffer(offer);
                      setEditOfferDialog(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    size="small"
                    onClick={() => {
                      setSelectedOfferToDelete(offer);
                      setDeleteOfferDialog(true);
                    }}
                    disabled={isDeletingOffer === offer.id}
                  >
                    {isDeletingOffer === offer.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Payments Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            px: 2,
            py: 8,
            gap: 2
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280' }}>
              Funkcjonalność płatności będzie dostępna wkrótce
            </Typography>
          </Box>
        </TabPanel>

        {/* Prefix Configuration Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            px: 2,
            py: 1.5,
            gap: 2
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
              Prefiksy ID zadań i kontaktów
            </Typography>
            <TextField
              placeholder="Szukaj prefiksów..."
              variant="outlined"
              size="small"
              value={prefixSearchTerm}
              onChange={(e) => setPrefixSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{
                minWidth: '200px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '999px',
                  height: '32px',
                  backgroundColor: '#fff',
                },
                '& .MuiInputBase-input': {
                  paddingY: '6px',
                  fontSize: '0.8rem',
                },
              }}
            />
          </Box>
          
          <List sx={{ p: 0 }}>
            {filteredPrefixConfig.map((config) => (
              <ListItem key={config.prefixType} divider sx={{ px: 2 }}>
                <ListItemText
                  primary={`${config.prefixType}: ${config.prefixValue}`}
                  secondary={`Aktualny prefiks dla ${config.prefixType?.toLowerCase()}`}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.8rem' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    size="small"
                    onClick={() => {
                      setEditingPrefix(config);
                      setEditPrefixDialog(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

      </Paper>

      {/* Dialogs */}
      
      {/* New User Dialog */}
      <Dialog open={newUserDialog} onClose={() => setNewUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj nowego użytkownika</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Login"
            fullWidth
            variant="outlined"
            value={newUser.login}
            onChange={(e) => setNewUser({ ...newUser, login: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Imię"
            fullWidth
            variant="outlined"
            value={newUser.firstName}
            onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Nazwisko"
            fullWidth
            variant="outlined"
            value={newUser.lastName}
            onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Hasło"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={newUser.password}
            onChange={(e) => handlePasswordChange('password', e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="Powtórz hasło"
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={newUser.confirmPassword}
            onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
            sx={{ mb: passwordErrors.length > 0 ? 1 : 0 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {passwordErrors.length > 0 && (
            <Box sx={{ mt: 1, mb: 1 }}>
              {passwordErrors.map((error, index) => (
                <Typography key={index} variant="caption" color="error" display="block">
                  • {error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewUserDialog(false)} disabled={isCreatingUser}>Anuluj</Button>
          <Button 
            onClick={handleCreateUser} 
            variant="contained" 
            disabled={isCreatingUser}
            startIcon={isCreatingUser ? <CircularProgress size={16} /> : undefined}
          >
            {isCreatingUser ? 'Tworzenie...' : 'Utwórz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Acquisition Type Dialog */}
      <Dialog open={newAcquisitionDialog} onClose={() => setNewAcquisitionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj źródło pozyskania</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa"
            fullWidth
            variant="outlined"
            value={newAcquisition.name}
            onChange={(e) => setNewAcquisition({ ...newAcquisition, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewAcquisitionDialog(false)} disabled={isCreatingAcquisition}>Anuluj</Button>
          <Button 
            onClick={handleCreateAcquisitionType} 
            variant="contained"
            disabled={isCreatingAcquisition}
            startIcon={isCreatingAcquisition ? <CircularProgress size={16} /> : undefined}
          >
            {isCreatingAcquisition ? 'Tworzenie...' : 'Utwórz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Acquisition Type Dialog */}
      <Dialog open={editAcquisitionDialog} onClose={() => setEditAcquisitionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edytuj źródło pozyskania</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa"
            fullWidth
            variant="outlined"
            value={editingAcquisition?.name || ''}
            onChange={(e) => setEditingAcquisition(prev => prev ? { ...prev, name: e.target.value } : null)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAcquisitionDialog(false)} disabled={isUpdatingAcquisition === editingAcquisition?.id}>Anuluj</Button>
          <Button 
            onClick={handleUpdateAcquisitionType} 
            variant="contained"
            disabled={isUpdatingAcquisition === editingAcquisition?.id}
            startIcon={isUpdatingAcquisition === editingAcquisition?.id ? <CircularProgress size={16} /> : undefined}
          >
            {isUpdatingAcquisition === editingAcquisition?.id ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Lead Attribute Dialog */}
      <Dialog open={newAttributeDialog} onClose={() => setNewAttributeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj atrybut kontaktu</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa atrybutu"
            fullWidth
            variant="outlined"
            value={newAttribute.attributeKey}
            onChange={(e) => setNewAttribute({ ...newAttribute, attributeKey: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Typ</InputLabel>
            <Select
              value={newAttribute.attributeType}
              label="Typ"
              onChange={(e) => setNewAttribute({ ...newAttribute, attributeType: e.target.value })}
            >
              <MenuItem value="TEXT">Tekst</MenuItem>
              <MenuItem value="NUMBER">Liczba</MenuItem>
              <MenuItem value="DATE">Data</MenuItem>
              <MenuItem value="BOOLEAN">Tak/Nie</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewAttributeDialog(false)} disabled={isCreatingAttribute}>Anuluj</Button>
          <Button 
            onClick={handleCreateLeadAttribute} 
            variant="contained"
            disabled={isCreatingAttribute}
            startIcon={isCreatingAttribute ? <CircularProgress size={16} /> : undefined}
          >
            {isCreatingAttribute ? 'Tworzenie...' : 'Utwórz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lead Attribute Dialog */}
      <Dialog open={editAttributeDialog} onClose={() => setEditAttributeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edytuj atrybut kontaktu</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa atrybutu"
            fullWidth
            variant="outlined"
            value={editingAttribute?.attributeKey || ''}
            onChange={(e) => setEditingAttribute(prev => prev ? { ...prev, attributeKey: e.target.value } : null)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Typ</InputLabel>
            <Select
              value={editingAttribute?.attributeType || 'TEXT'}
              label="Typ"
              onChange={(e) => setEditingAttribute(prev => prev ? { ...prev, attributeType: e.target.value } : null)}
            >
              <MenuItem value="TEXT">Tekst</MenuItem>
              <MenuItem value="NUMBER">Liczba</MenuItem>
              <MenuItem value="DATE">Data</MenuItem>
              <MenuItem value="BOOLEAN">Tak/Nie</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAttributeDialog(false)} disabled={isUpdatingAttribute === editingAttribute?.id}>Anuluj</Button>
          <Button 
            onClick={handleUpdateLeadAttribute} 
            variant="contained"
            disabled={isUpdatingAttribute === editingAttribute?.id}
            startIcon={isUpdatingAttribute === editingAttribute?.id ? <CircularProgress size={16} /> : undefined}
          >
            {isUpdatingAttribute === editingAttribute?.id ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Prefix Dialog */}
      <Dialog open={editPrefixDialog} onClose={() => setEditPrefixDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edytuj prefiks</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Prefiks"
            fullWidth
            variant="outlined"
            value={editingPrefix?.prefixValue || ''}
            onChange={(e) => setEditingPrefix(prev => prev ? { ...prev, prefixValue: e.target.value } : null)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPrefixDialog(false)} disabled={isUpdatingPrefix}>Anuluj</Button>
          <Button 
            onClick={handleUpdatePrefix} 
            variant="contained"
            disabled={isUpdatingPrefix}
            startIcon={isUpdatingPrefix ? <CircularProgress size={16} /> : undefined}
          >
            {isUpdatingPrefix ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Offer Dialog */}
      <Dialog open={newOfferDialog} onClose={() => setNewOfferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj ofertę</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa oferty"
            fullWidth
            variant="outlined"
            value={newOffer.name}
            onChange={(e) => setNewOffer({ ...newOffer, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Typ</InputLabel>
            <Select
              value={newOffer.type}
              label="Typ"
              onChange={(e) => setNewOffer({ ...newOffer, type: e.target.value as 'PRODUCT' | 'SERVICE' })}
            >
              <MenuItem value="PRODUCT">Produkt</MenuItem>
              <MenuItem value="SERVICE">Usługa</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Opis"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newOffer.description}
            onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Cena (zł)"
            fullWidth
            variant="outlined"
            type="number"
            value={newOffer.price}
            onChange={(e) => setNewOffer({ ...newOffer, price: parseFloat(e.target.value) || 0 })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOfferDialog(false)} disabled={isCreatingOffer}>Anuluj</Button>
          <Button 
            onClick={handleCreateOffer} 
            variant="contained"
            disabled={isCreatingOffer}
            startIcon={isCreatingOffer ? <CircularProgress size={16} /> : undefined}
          >
            {isCreatingOffer ? 'Tworzenie...' : 'Utwórz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Offer Dialog */}
      <Dialog open={editOfferDialog} onClose={() => setEditOfferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edytuj ofertę</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa"
            fullWidth
            variant="outlined"
            value={editingOffer?.name || ''}
            onChange={(e) => setEditingOffer(prev => prev ? { ...prev, name: e.target.value } : null)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Typ</InputLabel>
            <Select
              value={editingOffer?.type || 'PRODUCT'}
              label="Typ"
              onChange={(e) => setEditingOffer(prev => prev ? { ...prev, type: e.target.value as 'PRODUCT' | 'SERVICE' } : null)}
            >
              <MenuItem value="PRODUCT">Produkt</MenuItem>
              <MenuItem value="SERVICE">Usługa</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Opis"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editingOffer?.description || ''}
            onChange={(e) => setEditingOffer(prev => prev ? { ...prev, description: e.target.value } : null)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Cena (zł)"
            fullWidth
            variant="outlined"
            type="number"
            value={editingOffer?.price || 0}
            onChange={(e) => setEditingOffer(prev => prev ? { ...prev, price: parseFloat(e.target.value) || 0 } : null)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOfferDialog(false)} disabled={isUpdatingOffer === editingOffer?.id}>Anuluj</Button>
          <Button 
            onClick={handleUpdateOffer} 
            variant="contained"
            disabled={isUpdatingOffer === editingOffer?.id}
            startIcon={isUpdatingOffer === editingOffer?.id ? <CircularProgress size={16} /> : undefined}
          >
            {isUpdatingOffer === editingOffer?.id ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialogs */}
      
      {/* Delete User Dialog */}
      <DeleteConfirmDialog
        open={deleteUserDialog}
        onClose={handleCloseDeleteUserDialog}
        onConfirm={handleDeleteUser}
        title="Potwierdź usunięcie użytkownika"
        content={`Czy na pewno chcesz usunąć użytkownika "${selectedUserToDelete?.firstName} ${selectedUserToDelete?.lastName}" (${selectedUserToDelete?.email})? Tej operacji nie można cofnąć.`}
        isSubmitting={isDeletingUser === selectedUserToDelete?.id}
      />

      {/* Delete Acquisition Type Dialog */}
      <DeleteConfirmDialog
        open={deleteAcquisitionDialog}
        onClose={handleCloseDeleteAcquisitionDialog}
        onConfirm={handleDeleteAcquisitionType}
        title="Potwierdź usunięcie źrodła pozyskania"
        content={`Czy na pewno chcesz usunąć źródło pozyskania "${selectedAcquisitionToDelete?.name}"? Tej operacji nie można cofnąć.`}
        isSubmitting={isDeletingAcquisition === selectedAcquisitionToDelete?.id}
      />

      {/* Delete Lead Attribute Dialog */}
      <DeleteConfirmDialog
        open={deleteAttributeDialog}
        onClose={handleCloseDeleteAttributeDialog}
        onConfirm={handleDeleteLeadAttribute}
        title="Potwierdź usunięcie atrybutu"
        content={`Czy na pewno chcesz usunąć atrybut "${selectedAttributeToDelete?.attributeKey}"? Tej operacji nie można cofnąć.`}
        isSubmitting={isDeletingAttribute === selectedAttributeToDelete?.id}
      />

      {/* Delete Offer Dialog */}
      <DeleteConfirmDialog
        open={deleteOfferDialog}
        onClose={handleCloseDeleteOfferDialog}
        onConfirm={handleDeleteOffer}
        title="Potwierdź usunięcie oferty"
        content={`Czy na pewno chcesz usunąć ofertę "${selectedOfferToDelete?.name}"? Tej operacji nie można cofnąć.`}
        isSubmitting={isDeletingOffer === selectedOfferToDelete?.id}
      />


      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSettingsPage;